import React from "react"
import {
  Box,
  ButtonBase,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { ClientContextChips, type ClientChip } from "../molecules/ClientContextChips"
import { SidebarMenu, SIDEBAR_MENU_WIDTH } from "../molecules/SidebarMenu"
import { SidebarMenuItem } from "../molecules/SidebarMenuItem"
import { NavCollapseControl } from "../molecules/NavCollapseControl"
import { StartSessionButton } from "../molecules/StartSessionButton"
import { CircularAudioVisualizer } from "../molecules/CircularAudioVisualizer"
import type { StartSessionStatus } from "../molecules/StartSessionButton"
import { Heading } from "../atoms/ViraText"
import { Divider } from "../atoms/Divider"
import { AccentDot } from "../atoms/AccentDot"
import type { ViraIconName } from "../atoms/ViraIcon"
import type { ViraThemeMode } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type LoopNavSection = {
  id: string
  label: string
  badge?: string
  icon?: ViraIconName
  items?: Array<{
    id: string
    label: string
    icon?: ViraIconName
  }>
}

export type LoopSidebarProps = {
  clientName: string
  chips?: ClientChip[]
  activeItemId?: string
  sections?: LoopNavSection[]
  /** @deprecated Unused — clients nav lives in the app header. */
  onBackToClients?: () => void
  /** Idle: open consent / start recording. */
  onStartSession?: (event: React.MouseEvent<HTMLElement>) => void
  onPauseSession?: () => void
  onResumeSession?: () => void
  onStopSession?: () => void
  sessionStatus?: StartSessionStatus
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
  /** Canvas for live circular mic visualizer (imperative paint). */
  audioVisualizerCanvasRef?: React.MutableRefObject<HTMLCanvasElement | null>
  /** @deprecated Use `audioVisualizerCanvasRef`. */
  audioActivityIndicatorRef?: React.MutableRefObject<HTMLCanvasElement | null>
  onNavigate?: (itemId: string) => void
  /** Footer action — Account settings (pinned to bottom of sidebar). */
  onAccountSettings?: () => void
  accountSettingsActive?: boolean
  /** Color mode for the footer ThemeSwitcher. Parent owns `ViraThemeProvider`. */
  themeMode?: ViraThemeMode
  onThemeModeChange?: (mode: ViraThemeMode) => void
  /** Controlled collapse state */
  collapsed?: boolean
  /** Uncontrolled initial collapse (desktop). Below `md`, forced collapsed when uncontrolled. */
  defaultCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  /** @deprecated Prefer `onCollapsedChange` */
  onToggleCollapse?: () => void
}

/** Flat Loop nav — no Treat/Wrap-up group headers. */
export const DEFAULT_LOOP_SECTIONS: LoopNavSection[] = [
  { id: "prepare", label: "Prepare", icon: "assessment", badge: "today" },
  { id: "summary", label: "Summary", icon: "note" },
  { id: "calendar", label: "Calendar", icon: "calendar" },
  { id: "sleep", label: "Sleep", icon: "sleep" },
  { id: "activity", label: "Activity", icon: "activity" },
  { id: "language", label: "Language", icon: "language" },
  { id: "assessments", label: "Assessments", icon: "assessment" },
  { id: "dbt-diary", label: "DBT diary cards", icon: "diary" },
  { id: "safety-plan", label: "Safety plan", icon: "plan" },
  { id: "relapse", label: "Relapse prevention", icon: "relapse" },
  { id: "past-sessions", label: "Sessions", icon: "mic" },
]

export function LoopSidebar({
  clientName,
  chips,
  activeItemId = "summary",
  sections = DEFAULT_LOOP_SECTIONS,
  onStartSession,
  onPauseSession,
  onResumeSession,
  onStopSession,
  sessionStatus = "idle",
  registerElapsedTimeElement,
  audioVisualizerCanvasRef,
  audioActivityIndicatorRef,
  onNavigate,
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onToggleCollapse,
  onCollapsedChange,
  onAccountSettings,
  accountSettingsActive = false,
  themeMode = "light",
  onThemeModeChange,
}: LoopSidebarProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isControlled = collapsedProp !== undefined
  const [uncontrolled, setUncontrolled] = React.useState(defaultCollapsed)
  /**
   * When uncontrolled: below `md`, force the collapsed rail (same contract as
   * `SidebarMenu`). Pass `collapsed={false}` inside a drawer to show labels.
   */
  const collapsed = isControlled
    ? Boolean(collapsedProp)
    : isMobile
      ? true
      : uncontrolled

  const handleToggleCollapse = React.useCallback(() => {
    const next = !collapsed
    if (!isControlled) setUncontrolled(next)
    onCollapsedChange?.(next)
    onToggleCollapse?.()
  }, [collapsed, isControlled, onCollapsedChange, onToggleCollapse])

  const width = collapsed
    ? SIDEBAR_MENU_WIDTH.collapsed
    : SIDEBAR_MENU_WIDTH.expanded

  const isCaptureLive =
    sessionStatus === "recording" || sessionStatus === "paused"
  const visualizerCanvasRef =
    audioVisualizerCanvasRef ?? audioActivityIndicatorRef

  return (
    <Box
      component="nav"
      aria-label="Client navigation"
      data-collapsed={collapsed ? "true" : "false"}
      sx={(theme) => ({
        width,
        maxWidth: "100%",
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${resolveViraPalette(theme).border}`,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        transition: "width 160ms ease",
        overflow: "hidden",
      })}
    >
      <Stack
        direction="row"
        justifyContent={collapsed ? "center" : "flex-end"}
        alignItems="center"
        sx={{ px: collapsed ? 1 : 3, pt: 2.75, pb: collapsed ? 0.5 : 0 }}
      >
        <NavCollapseControl
          collapsed={collapsed}
          onClick={handleToggleCollapse}
        />
      </Stack>

      {!collapsed ? (
        <Box sx={{ px: 3, pt: 1.5, pb: 1 }}>
          <Heading level="page" sx={{ mb: 1 }}>
            {clientName}
          </Heading>
          <ClientContextChips chips={chips} />
        </Box>
      ) : null}

      <Box sx={{ px: collapsed ? 1 : 3, py: 2 }}>
        {collapsed ? (
          <Tooltip
            title={
              sessionStatus === "recording"
                ? "Recording — expand to control"
                : sessionStatus === "paused"
                  ? "Paused — expand to control"
                  : sessionStatus === "uploading" ||
                      sessionStatus === "processing"
                    ? "Processing transcript"
                    : "Start session"
            }
            placement="right"
          >
            <ButtonBase
              onClick={
                sessionStatus === "idle"
                  ? onStartSession
                  : sessionStatus === "recording"
                    ? onPauseSession
                    : sessionStatus === "paused"
                      ? onResumeSession
                      : undefined
              }
              aria-label="Session control"
              sx={(theme) => ({
                width: "100%",
                height: 40,
                minHeight: 40,
                borderRadius: "8px",
                backgroundColor: isCaptureLive
                  ? "#FDF1E8"
                  : resolveViraPalette(theme).lavender,
                border: `1px solid ${
                  isCaptureLive
                    ? "#F5C9A6"
                    : resolveViraPalette(theme).lavenderBorder
                }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              })}
            >
              {isCaptureLive ? (
                <CircularAudioVisualizer
                  canvasRef={visualizerCanvasRef}
                  paused={sessionStatus === "paused"}
                  size={28}
                  label={
                    sessionStatus === "paused"
                      ? "Microphone paused"
                      : "Live microphone level"
                  }
                />
              ) : (
                <AccentDot size={7} />
              )}
            </ButtonBase>
          </Tooltip>
        ) : (
          <StartSessionButton
            status={sessionStatus}
            onStart={onStartSession}
            onPause={onPauseSession}
            onResume={onResumeSession}
            onStop={onStopSession}
            registerElapsedTimeElement={registerElapsedTimeElement}
            audioVisualizerCanvasRef={visualizerCanvasRef}
          />
        )}
      </Box>

      <Divider />

      <SidebarMenu
        collapsed={collapsed}
        data-loop-scroll=""
        sx={{
          flex: 1,
          minHeight: 0,
          // Independent scroll when nav exceeds the viewport. `data-loop-scroll`
          // keeps wheel gestures local (LoopShell allow-list).
          overflowX: "hidden",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {sections.map((section) => {
          const sectionActive = activeItemId === section.id
          const isGroup = Boolean(section.items?.length)
          return (
            <Box key={section.id}>
              <SidebarMenuItem
                variant={isGroup ? "section" : "item"}
                group={isGroup}
                depth={isGroup ? undefined : 0}
                label={section.label}
                active={!isGroup && sectionActive}
                badge={section.badge}
                icon={section.icon}
                onClick={
                  isGroup ? undefined : () => onNavigate?.(section.id)
                }
                sx={
                  isGroup
                    ? {
                        cursor: "default",
                        "&:hover": { backgroundColor: "transparent" },
                      }
                    : undefined
                }
              />
              {section.items?.map((item) => (
                <SidebarMenuItem
                  key={item.id}
                  label={item.label}
                  active={activeItemId === item.id}
                  icon={item.icon}
                  onClick={() => onNavigate?.(item.id)}
                />
              ))}
            </Box>
          )
        })}

        <Box sx={{ px: collapsed ? 1 : 3, py: 1.5 }}>
          <Divider />
        </Box>

        <SidebarMenuItem
          label="Light"
          icon="sun"
          depth={0}
          collapsed={collapsed}
          active={themeMode === "light"}
          onClick={() => onThemeModeChange?.("light")}
          aria-label="Light mode"
        />
        <SidebarMenuItem
          label="Dark"
          icon="moon"
          depth={0}
          collapsed={collapsed}
          active={themeMode === "dark"}
          onClick={() => onThemeModeChange?.("dark")}
          aria-label="Dark mode"
        />
        <SidebarMenuItem
          label="Account settings"
          icon="settings"
          depth={0}
          collapsed={collapsed}
          active={accountSettingsActive}
          onClick={onAccountSettings}
        />
      </SidebarMenu>
    </Box>
  )
}
